/**
 * Allergy / chronic-condition conflict checker for prescription safety.
 *
 * Catches the common families that cause harm in primary care:
 *   - direct name match (e.g., patient allergic to "Aspirin" → "Aspirin 75mg")
 *   - substring/word match (e.g., "Penicillin" → "Amoxicillin" via class map)
 *   - generic + brand variants via a small curated drug-class map
 *
 * No backend round-trip per keystroke — runs locally against the patient
 * profile's `allergies` array loaded once on consultation open.
 *
 * Returns an array of conflicts:
 *   { medicine: <orig>, allergy: <orig>, reason: 'direct'|'class', class?: <name> }
 */

// Curated mapping. Each key is a normalised allergy token (lowercase, no spaces);
// each value is a list of substrings to look for inside the medicine name.
// Intentionally conservative — false negatives are safer than false positives
// for class warnings (we still flag exact name matches separately).
const DRUG_CLASSES = {
  penicillin: ['penicillin', 'amoxicillin', 'amoxycillin', 'ampicillin', 'cloxacillin',
               'flucloxacillin', 'piperacillin', 'oxacillin', 'augmentin', 'amoxiclav', 'co-amoxiclav'],
  cephalosporin: ['cef', 'cephalexin', 'cefuroxime', 'cefixime', 'cefpodoxime',
                  'ceftriaxone', 'ceftazidime', 'cefepime', 'cefadroxil', 'cefaclor'],
  sulfa: ['sulfa', 'sulpha', 'sulfamethoxazole', 'sulfonamide', 'cotrimoxazole',
          'co-trimoxazole', 'bactrim', 'septra', 'trimethoprim'],
  nsaid: ['ibuprofen', 'naproxen', 'diclofenac', 'aspirin', 'indomethacin',
          'ketoprofen', 'ketorolac', 'mefenamic', 'piroxicam', 'celecoxib', 'etoricoxib', 'nimesulide'],
  aspirin: ['aspirin', 'asa', 'acetylsalicylic', 'ecosprin', 'disprin'],
  ibuprofen: ['ibuprofen', 'brufen'],
  paracetamol: ['paracetamol', 'acetaminophen', 'crocin', 'dolo', 'calpol', 'tylenol'],
  macrolide: ['azithromycin', 'erythromycin', 'clarithromycin', 'roxithromycin'],
  quinolone: ['ciprofloxacin', 'levofloxacin', 'moxifloxacin', 'norfloxacin', 'ofloxacin', 'floxacin'],
  fluoroquinolone: ['ciprofloxacin', 'levofloxacin', 'moxifloxacin', 'norfloxacin', 'ofloxacin'],
  tetracycline: ['tetracycline', 'doxycycline', 'minocycline'],
  statin: ['atorvastatin', 'rosuvastatin', 'simvastatin', 'pravastatin', 'lovastatin'],
  opioid: ['morphine', 'codeine', 'tramadol', 'oxycodone', 'fentanyl', 'pethidine', 'hydrocodone'],
  ace: ['enalapril', 'ramipril', 'lisinopril', 'captopril', 'perindopril'],
  arb: ['losartan', 'telmisartan', 'valsartan', 'olmesartan', 'irbesartan'],
};

const _norm = (s) => (s || '').toString().toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * Return conflict objects for any medicine in `medicines` that overlaps an
 * `allergies` entry. `medicines` items may be strings or objects with
 * `medicine_name` / `name` keys.
 */
export function findAllergyConflicts(allergies, medicines) {
  if (!allergies?.length || !medicines?.length) return [];
  const out = [];

  for (const med of medicines) {
    const rawName = typeof med === 'string' ? med : (med?.medicine_name || med?.name || '');
    const medName = _norm(rawName);
    if (!medName) continue;

    for (const allergy of allergies) {
      const aName = _norm(allergy);
      if (!aName) continue;

      // 1. Direct substring (covers exact name + common brand prefixes).
      // Either direction — "penicillin" allergy matches "Penicillin V 250" AND
      // "Amoxicillin/Penicillin" matches "penicillin".
      const directHit =
        medName.includes(aName) ||
        aName.split(' ').some(tok => tok.length >= 4 && medName.includes(tok));
      if (directHit) {
        out.push({
          medicine: rawName,
          allergy,
          reason: 'direct',
          severity: 'high',
        });
        continue;
      }

      // 2. Drug-class lookup. Find the canonical class key for the allergy
      // (penicillin / sulfa / nsaid / …) and check if the med belongs to it.
      const aKey = aName.replace(/\s+/g, '');
      const classKey = Object.keys(DRUG_CLASSES).find(k =>
        aKey.includes(k) || k.includes(aKey)
      );
      if (classKey) {
        const hit = DRUG_CLASSES[classKey].some(sub => medName.includes(sub));
        if (hit) {
          out.push({
            medicine: rawName,
            allergy,
            reason: 'class',
            class: classKey,
            severity: 'medium',
          });
        }
      }
    }
  }
  return out;
}

/**
 * Quick boolean — true if any medicine collides with any allergy.
 */
export function hasAnyAllergyConflict(allergies, medicines) {
  return findAllergyConflicts(allergies, medicines).length > 0;
}
